import type { JSX } from "react";
import { TooltipV2 } from "@/components/TooltipV2";
import { type IconName } from "@/components/icons/Icon";
import {
  DropdownImplV2,
  type SharedDropdownV2Props,
  type SingleDropdownImplV2Props,
} from "@/components/interactive/DropdownImplV2";
import type { DropdownEntry, DropdownOption } from "@/components/interactive/DropdownV2.types";
import { isDropdownOptionGroup } from "@/components/interactive/dropdown-option-utils";
import { type SelectV2Variant } from "@/components/interactive/SelectV2";
import React, { useMemo } from "react";

// ------------------------------------------------------------------
// SelectMenuV2 — a dropdown with no typing: pick from a list you can read at a
// glance. `variant` chooses how many values you can hold at once.
//
// Search behaviour is the component, cardinality is the variant. This one is
// never searchable; reach for ComboboxV2 when the list is long enough to want a
// filter. Both wrap the same DropdownImplV2, so this file only maps a friendlier
// prop surface onto it — it owns no menu behaviour of its own, which is why it
// used to have no keyboard support at all.
// ------------------------------------------------------------------

export type SelectMenuV2SharedProps<T extends string | number = string> =
  SharedDropdownV2Props<T> & {
    /** When provided and the control is disabled, wraps the trigger in a tooltip. */
    disabledTooltip?: string;
    label?: string;
    description?: string;
    /** Flat rows, or `{ label, options }` groups the menu draws as titled sections. */
    options: readonly DropdownEntry<T>[];
    testId?: string;
    /** Render the first option in its own section, visually separated from the rest. Flat only. */
    separateFirstOption?: boolean;
  };

// The leading icon and the trigger chrome are single-only, mirroring the impl:
// the multi trigger is a chip host with no room for an icon and no chevron to
// restyle, so accepting them there would silently drop them.
type SelectMenuV2SingleChrome<T extends string | number> = {
  leftIcon?: IconName;
  leftIconClassName?: string;
  triggerTextVariant?: SingleDropdownImplV2Props<string>["triggerTextVariant"];
  /**
   * Trigger chrome — see SelectV2Props.variant. Named to match ComboboxV2,
   * where `variant` is cardinality and `triggerVariant` is chrome; the two
   * components would be a trap if `variant` meant opposite things.
   */
  triggerVariant?: SelectV2Variant;
  hideChevron?: boolean;
  placeholderValue?: T;
};

// `includeNoneOption` mirrors SearchableSelectMenuV2's own union exactly:
// nullability follows it, so `onChange` can only ever be asked to handle
// `null` where the None row exists to produce it. A multi trigger has no
// analogous state — it already clears one value at a time by removing its
// chip — so it stays out of this split entirely.
export type SelectMenuV2SingleProps<T extends string | number = string> =
  | (SelectMenuV2SharedProps<T> &
      SelectMenuV2SingleChrome<T> & {
        variant?: "single";
        includeNoneOption?: false;
        value: T;
        onChange: (value: T) => void;
      })
  | (SelectMenuV2SharedProps<T> &
      SelectMenuV2SingleChrome<T> & {
        variant?: "single";
        includeNoneOption: true;
        value: T | null;
        onChange: (value: T | null) => void;
      });

export type SelectMenuV2MultiProps<T extends string | number = string> =
  SelectMenuV2SharedProps<T> & {
    variant: "multi";
    value: readonly T[];
    onChange: (value: T[]) => void;
    /** Maximum number of selected values to render as removable chips. */
    displayLimit?: number;
  };

export type SelectMenuV2Props<T extends string | number = string> =
  | SelectMenuV2SingleProps<T>
  | SelectMenuV2MultiProps<T>;

// A plain union prop type left every existing single caller's `onChange`
// inferring as `any`: two arms (no `includeNoneOption` vs `includeNoneOption:
// true`) both leaving `variant` optional gave TypeScript nothing to
// discriminate on for a caller that mentions neither prop, so it couldn't pick
// an arm before checking that caller's `onChange` against it. Overloaded call
// signatures sidestep this: each is tried against the actual props object in
// order, as a normal function call would be, rather than requiring the props
// object to first resolve against a merged union type.
interface SelectMenuV2Component {
  <T extends string | number = string>(
    props: SelectMenuV2SharedProps<T> &
      SelectMenuV2SingleChrome<T> & {
        variant?: "single";
        includeNoneOption?: false;
        value: T;
        onChange: (value: T) => void;
      },
  ): JSX.Element;
  <T extends string | number = string>(
    props: SelectMenuV2SharedProps<T> &
      SelectMenuV2SingleChrome<T> & {
        variant?: "single";
        includeNoneOption: true;
        value: T | null;
        onChange: (value: T | null) => void;
      },
  ): JSX.Element;
  <T extends string | number = string>(props: SelectMenuV2MultiProps<T>): JSX.Element;
}

const SelectMenuV2Impl = <T extends string | number = string>(
  props: SelectMenuV2Props<T>,
): JSX.Element => {
  const {
    className,
    disabled = false,
    disabledTooltip,
    label,
    description,
    labelTrailingAction,
    required = false,
    options,
    placeholder,
    separateFirstOption = false,
    loading = false,
    size = "sm",
    triggerAriaLabel,
    error,
    onBlur,
    onScrollBottom,
    loadingMore,
    itemRenderer,
    valueRenderer,
    containerClassName,
    emptyMessage = "No results",
    menuClassName,
    menuWidth,
    menuPlacement,
    menuZIndex,
    forceIndent,
    noneOptionMuted,
    testId,
  } = props;

  const entries = useMemo<DropdownEntry<T>[]>(() => {
    // `separateFirstOption` is a one-option group ahead of the rest, which is
    // what DropdownImplV2's grouping already draws a separator for.
    if (separateFirstOption && options.length > 1 && !options.some(isDropdownOptionGroup)) {
      const [first, ...rest] = options as readonly DropdownOption<T>[];
      return [
        { label: "", options: [first] },
        { label: "", options: rest },
      ];
    }

    return [...options];
  }, [options, separateFirstOption]);

  const shared = {
    label,
    description,
    labelTrailingAction,
    required,
    triggerAriaLabel,
    error,
    onBlur,
    onScrollBottom,
    loadingMore,
    size,
    placeholder,
    options: entries,
    itemRenderer,
    valueRenderer,
    disabled,
    loading,
    className,
    containerClassName,
    menuClassName,
    menuWidth,
    menuPlacement,
    menuZIndex,
    emptyMessage,
    forceIndent,
    noneOptionMuted,
    testId,
  };

  const select =
    props.variant === "multi" ? (
      <DropdownImplV2<T>
        {...shared}
        mode={{ kind: "select", includeNoneOption: false }}
        variant={"multi"}
        value={props.value}
        onChange={props.onChange}
        displayLimit={props.displayLimit}
      />
    ) : (
      <DropdownImplV2<T>
        {...shared}
        mode={{ kind: "select", includeNoneOption: props.includeNoneOption ?? false }}
        leftIcon={props.leftIcon}
        leftIconClassName={props.leftIconClassName}
        triggerTextVariant={props.triggerTextVariant}
        triggerVariant={props.triggerVariant ?? "default"}
        hideChevron={props.hideChevron}
        placeholderValue={props.placeholderValue}
        value={props.value}
        onChange={(next) => {
          // Null only ever comes from the None row, which only exists when the
          // caller opted in — so the narrow single-arm contract holds.
          if (next == null) {
            if (props.includeNoneOption) {
              props.onChange(null);
            }
            return;
          }
          (props.onChange as (committed: T) => void)(next);
        }}
      />
    );

  return disabled && disabledTooltip ? (
    <TooltipV2 content={disabledTooltip} placement={"bottom-start"}>
      <span className={"inline-flex [&_*]:pointer-events-none"}>{select}</span>
    </TooltipV2>
  ) : (
    select
  );
};

// Cast, not a JSX-checked declaration: the implementation genuinely takes the
// full union, but its public type is the overloaded interface above so
// call-site checking gets overload resolution instead of union discrimination.
export const SelectMenuV2 = SelectMenuV2Impl as SelectMenuV2Component;
